import React from 'react';

const BulletinViewModal = ({ bulletin, isOpen, onClose }) => {
  // Priority color mapping
  const priorityColors = {
    urgent: 'bg-red-600',
    high: 'bg-red-500',
    medium: 'bg-yellow-500',
    low: 'bg-green-500',
  };

  if (!isOpen || !bulletin) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl transform transition-all duration-300 scale-100" style={{ backgroundColor: '#212C43' }}>
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 md:top-4 md:right-4 z-20 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-all duration-200"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Hero Image Section */}
        {bulletin.imageUrl && (
          <div className="relative h-48 sm:h-64 md:h-80 overflow-hidden">
            <img 
              className="w-full h-full object-cover" 
              src={bulletin.imageUrl} 
              alt={bulletin.title}
              // onError={(e) => {
              //   e.target.src = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop';
              // }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
            
            {/* Priority Badge */}
            {/* <div className={`absolute top-4 right-4 px-4 py-2 rounded-full text-sm font-bold text-white ${priorityColors[bulletin.priority] || 'bg-gray-500'}`}>
              {bulletin.priority.toUpperCase()} PRIORITY
            </div> */}
            
            {/* Category Badge */}
            <div className="absolute top-3 right-12 md:top-4 md:right-16 px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-semibold text-white bg-blue-600/80 backdrop-blur-sm z-10">
              {bulletin.category}
            </div>
            
            {/* Title Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">
                {bulletin.title}
              </h1>
            </div>
          </div>
        )}

        {/* Content Section */}
        <div className="p-4 sm:p-6 md:p-8">
          {/* Meta Information */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 pb-6 border-b border-gray-600 gap-4 sm:gap-0">
            <div className="flex flex-wrap items-center gap-3 md:gap-4">
              <div className="flex items-center text-gray-300">
                <svg className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0h6m-6 0l-2 2m8-2l2 2m-2 0h2m-2 0V7m0 8v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4m12-2h2m-2 0V9m-2 2l2-2m-2 2l-2-2" />
                </svg>
                <span className="text-xs md:text-sm">Bulletin #{bulletin.id}</span>
              </div>
              <div className="flex items-center text-gray-300">
                <svg className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-xs md:text-sm">Published: {new Date().toLocaleDateString()}</span>
              </div>
            </div>
            
            <div className={`px-3 py-1 rounded-full text-xs font-semibold text-white w-fit ${priorityColors[bulletin.priority] || 'bg-gray-500'}`}>
              {bulletin.priority.toUpperCase()}
            </div>
          </div>

          {/* Description */}
          <div className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-xl font-bold text-white mb-3 md:mb-4">Summary</h2>
            <p className="text-gray-300 leading-relaxed text-base md:text-lg">
              {bulletin.description}
            </p>
          </div>

          {/* Detailed Content */}
          {bulletin.content && (
            <div className="mb-6 md:mb-8">
              <h2 className="text-xl font-bold text-white mb-4">Detailed Information</h2>
              <div className="text-gray-300 leading-relaxed whitespace-pre-line">
                {bulletin.content}
              </div>
            </div>
          )}

          {/* Attachments */}
          {Array.isArray(bulletin.attachments) && bulletin.attachments.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-white mb-4">Attachments</h2>
              <ul className="space-y-2">
                {bulletin.attachments.map((att, idx) => (
                  <li key={idx} className="flex items-center justify-between text-sm text-gray-300 bg-[#384355] px-3 py-2 rounded">
                    <span className="truncate flex-1">{att.name || `Attachment ${idx + 1}`}</span>
                    <a
                      href={att.url || '#'}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-3 text-blue-400 hover:text-blue-300"
                    >
                      View
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          {/* <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-600">
            <button className="flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
              Share Bulletin
            </button>
            
            <button className="flex items-center justify-center px-6 py-3 border border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white rounded-lg transition-all duration-200">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download PDF
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default BulletinViewModal;